import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { studentApi } from '@/api';
import { resolveFileUrl } from '@/api/client';
import {
  ActionMenu,
  ActionMenuItem,
  Badge,
  BoxRow,
  Button,
  Card,
  CardActions,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  FileUploader,
  Icon,
  LoadingState,
  PageHeader,
  useToast,
} from '@/components/ui';
import { RESUME_ACCEPT, RESUME_HINT, RESUME_MAX_SIZE } from '@/features/resume/upload';
import { DOCUMENT_TONES, STUDENT_DOCUMENT_LABELS } from '@/features/verification/status';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';
import { formatDate, formatFileSize } from '@/utils/format';

/** The file type, read off the name: "PDF", "DOCX". */
function extensionOf(name) {
  const extension = name?.split('.').pop();
  return extension && extension !== name ? extension.toUpperCase() : 'FILE';
}

/** PDFs in the danger tint, Word files in the info tint -- as file managers do. */
function fileTone(extension) {
  if (extension === 'PDF') return 'bg-danger-50 text-danger-600 ring-danger-100';
  if (extension === 'DOC' || extension === 'DOCX') return 'bg-info-50 text-info-700 ring-info-100';
  return 'bg-surface-muted text-ink-muted ring-line';
}

/** What reading the CV produced, said as a badge. */
const PARSE_BADGES = {
  READY: { tone: 'success', label: 'Suggestions ready' },
  PENDING: { tone: 'warning', label: 'Reading…' },
  FAILED: { tone: 'danger', label: 'Could not read' },
};

const GOOD_TO_KNOW = [
  {
    icon: 'eyeOff',
    title: 'Who can see this?',
    body: 'Your CV is private. Companies receive a time-limited link only when you apply to their internship.',
  },
  {
    icon: 'shieldCheck',
    title: 'Get it verified',
    body: 'Our team checks every CV you upload. A verified CV tells companies the facts on it have been confirmed.',
  },
  {
    icon: 'star',
    title: 'Save yourself the typing',
    body: 'We can read your CV and suggest skills, education and experience for your profile. Nothing is added until you tick it.',
  },
];

export function StudentCvPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [deleting, setDeleting] = useState(null);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['student-resumes'],
    queryFn: studentApi.listResumes,
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['student-resumes'] });
    void queryClient.invalidateQueries({ queryKey: ['student-dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['student-profile'] });
  }

  const upload = useMutation({
    mutationFn: (file) => studentApi.uploadResume(file),
    onSuccess: () => {
      toast.success(
        'CV uploaded. Use "Fill my profile from this CV" to add its details to your profile.',
      );
      invalidate();
    },
    onError: (uploadError) => toast.error(errorMessage(uploadError, 'Upload failed')),
  });

  const remove = useMutation({
    mutationFn: (id) => studentApi.deleteResume(id),
    onSuccess: () => {
      toast.success('CV deleted');
      setDeleting(null);
      invalidate();
    },
    onError: (deleteError) => toast.error(errorMessage(deleteError)),
  });

  // Reading a CV changes nothing on its own -- it produces suggestions, and
  // the review screen is where the student decides what to do with them.
  const read = useMutation({
    mutationFn: (id) => studentApi.parseResume(id),
    onSuccess: (parse) => {
      queryClient.setQueryData(['resume-parse', parse.resume_id], parse);
      invalidate();
      navigate(paths.student.cvReview(parse.resume_id));
    },
    onError: (readError) => toast.error(errorMessage(readError, 'Could not read the file')),
  });

  const setPrimary = useMutation({
    mutationFn: (id) => studentApi.setPrimaryResume(id),
    onSuccess: () => {
      toast.success('Primary CV updated');
      invalidate();
    },
    onError: (primaryError) => toast.error(errorMessage(primaryError)),
  });

  async function download(resume) {
    try {
      // URLs are signed and short-lived, so fetch a fresh one per click.
      const { download_url } = await studentApi.getResumeDownloadUrl(resume.id);
      window.open(resolveFileUrl(download_url), '_blank', 'noopener');
    } catch (downloadError) {
      toast.error(errorMessage(downloadError, 'Could not open the file'));
    }
  }

  if (isPending) return <LoadingState label="Loading your CV…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  return (
    <div>
      <PageHeader
        title="CV"
        description="Upload your CV once and reuse it for every application."
      />

      {/* items-start: the list is as tall as its CVs, not as tall as the
          sidebar beside it. */}
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <Card>
          <CardHeader
            title="Your CVs"
            description="Your primary CV is the one sent with every application."
            action={
              data.length > 0 && (
                <Badge tone="neutral">
                  {data.length} file{data.length === 1 ? '' : 's'}
                </Badge>
              )
            }
          />
          {data.length === 0 ? (
            <EmptyState
              icon={<Icon name="document" size={22} />}
              title="No CV uploaded yet"
              description="Upload one on the right. PDF, DOC or DOCX up to 5 MB."
            />
          ) : (
            // The same card as every other list: details on top, the everyday
            // actions under the line, and the file's own housekeeping
            // (download, make primary, delete) in the top-right corner.
            <ul className="space-y-3">
              {data.map((resume) => {
                const extension = extensionOf(resume.file_name);
                const parsed = PARSE_BADGES[resume.parse_status];
                const verification = resume.verification_status ?? 'UNVERIFIED';
                // A student may ask again after a refusal; never twice in a row.
                return (
                  <BoxRow
                    key={resume.id}
                    accent={resume.is_primary ? 'brand' : 'neutral'}
                    leading={
                      <span
                        aria-hidden="true"
                        className={cn(
                          'relative grid size-11 shrink-0 place-items-center rounded-lg ring-1 ring-inset',
                          fileTone(extension),
                        )}
                      >
                        <Icon name="document" size={20} />
                        <span className="bg-surface absolute -bottom-1.5 rounded px-1 text-[9px] leading-3.5 font-bold tracking-wide shadow-sm">
                          {extension}
                        </span>
                      </span>
                    }
                    // One line, cut with an ellipsis: a file name has no spaces,
                    // so wrapping broke it mid-word on a phone. The full name
                    // is on hover.
                    title={
                      <span className="block max-w-full truncate" title={resume.file_name}>
                        {resume.file_name}
                      </span>
                    }
                    subtitle={`${formatFileSize(resume.size_bytes)} · uploaded ${formatDate(resume.created_at)}`}
                    badges={
                      <>
                        {resume.is_primary && (
                          <Badge tone="brand" dot>
                            Primary
                          </Badge>
                        )}
                        {parsed && (
                          <Badge tone={parsed.tone} dot>
                            {parsed.label}
                          </Badge>
                        )}
                        <Badge tone={DOCUMENT_TONES[verification] ?? 'neutral'} dot>
                          {STUDENT_DOCUMENT_LABELS[verification] ?? verification}
                        </Badge>
                      </>
                    }
                    // The upload date is already in the subtitle, so the
                    // footer carries only the actions. Filling the profile is
                    // the step students missed behind an unlabelled icon, so it
                    // is spelled out; "Score & improve" is the main action.
                    actions={
                      <CardActions>
                        {resume.parse_status === 'FAILED' ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate(paths.student.cvReview(resume.id))}
                          >
                            <Icon name="alert" size={14} />
                            Could not read this CV
                          </Button>
                        ) : resume.parse_status ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate(paths.student.cvReview(resume.id))}
                          >
                            <Icon name="userCheck" size={14} />
                            Review profile suggestions
                          </Button>
                        ) : (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => read.mutate(resume.id)}
                            isLoading={read.isPending && read.variables === resume.id}
                          >
                            <Icon name="userCheck" size={14} />
                            Fill my profile from this CV
                          </Button>
                        )}
                        <Button
                          variant="secondary"
                          size="sm"
                          title={`Download ${resume.file_name}`}
                          onClick={() => void download(resume)}
                        >
                          <Icon name="download" size={14} />
                          Download
                        </Button>
                        {/* A fourth button only on a CV that is not primary yet,
                            so it waits in "More" rather than crowding the row. */}
                        <ActionMenu label={`More actions for ${resume.file_name}`}>
                          {!resume.is_primary && (
                            <ActionMenuItem
                              icon="check"
                              disabled={setPrimary.isPending}
                              onSelect={() => setPrimary.mutate(resume.id)}
                            >
                              Make this my primary CV
                            </ActionMenuItem>
                          )}
                        </ActionMenu>
                        <Button
                          variant="tintBrand"
                          size="sm"
                          onClick={() => navigate(paths.student.cvFeedback(resume.id))}
                        >
                          <Icon name="sparkles" size={14} />
                          Score &amp; improve
                        </Button>
                      </CardActions>
                    }
                    corner={
                      <Button
                        variant="ghostDanger"
                        size="sm"
                        title={`Delete ${resume.file_name}`}
                        onClick={() => setDeleting(resume)}
                      >
                        <Icon name="trash" size={14} />
                        Delete
                      </Button>
                    }
                  >
                    {verification === 'REJECTED' && resume.verification_rejection_reason && (
                      <p className="text-danger-700 mt-2.5 text-xs">
                        <span className="font-semibold">Not accepted: </span>
                        {resume.verification_rejection_reason}
                      </p>
                    )}
                  </BoxRow>
                );
              })}
            </ul>
          )}
        </Card>

        <div className="space-y-5">
          <Card>
            <CardHeader title="Upload a CV" description="A new file does not replace the others." />
            <FileUploader
              accept={RESUME_ACCEPT}
              maxSizeBytes={RESUME_MAX_SIZE}
              isUploading={upload.isPending}
              hint={RESUME_HINT}
              onSelect={(file) => upload.mutate(file)}
            />
          </Card>

          <Card>
            <CardHeader title="Good to know" />
            {/* Notes, not features: small quiet icons and a hairline between
                them, so the card reads as help rather than a second menu. */}
            <ul className="divide-line divide-y">
              {GOOD_TO_KNOW.map((item) => (
                <li key={item.title} className="flex gap-3 py-3 first:pt-0 last:pb-0">
                  <span
                    aria-hidden="true"
                    className="bg-surface-muted text-brand-600 ring-line grid size-7 shrink-0 place-items-center rounded-md ring-1 ring-inset"
                  >
                    <Icon name={item.icon} size={14} strokeWidth={2} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-ink text-[13px] font-medium">{item.title}</p>
                    <p className="text-ink-muted mt-0.5 text-xs leading-5">{item.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>

      <ConfirmDialog
        open={deleting !== null}
        title="Delete this CV?"
        message="Companies you already applied to keep the copy they received."
        confirmLabel="Delete"
        isLoading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
      />
    </div>
  );
}
